// One route per Vue component originally registered in
// popinwebapp/resources/js/app.js. The component itself comes from
// src/legacy-components/ unchanged; we just wrap it in a thin route that
// passes auth-store props (branchname/coname/cocode) where needed.
//
// Order, naming, and props mirror the original Blade embeds — keep this in
// sync if the Laravel team adds another global Vue.component(...) registration.

const lazy = (name) => () => import(`@/legacy-components/${name}.vue`)

// All admin/vendor/tech tooling expects these props. AppShell hydrates them
// from the auth store so individual routes don't have to.
const SHELL_PROPS = (route) => ({
  branchname: route.meta.branchname,
  coname:     route.meta.coname,
  cocode:     route.meta.cocode,
  loc:        route.meta.loc,
  abr:        route.meta.abr,
})

function shellRoute(path, name, file, extraProps = null) {
  return {
    path,
    name,
    component: lazy(file),
    props: (route) => ({
      ...SHELL_PROPS(route),
      ...(extraProps ? extraProps(route) : {}),
    }),
    meta: { requiresAuth: true },
  }
}

export const routes = [
  // Auth (the only routes that *don't* need an existing session)
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { guestOnly: true },
  },

  // Dashboard landing — picks the right legacy component based on role
  {
    path: '/',
    name: 'home',
    component: () => import('@/pages/HomeRedirect.vue'),
    meta: { requiresAuth: true },
  },

  // Rental admin dashboard — mirrors /admin/rental/dashboard from popinweb
  {
    path: '/rental/dashboard',
    name: 'rental-dashboard',
    component: () => import('@/pages/RentalDashboard.vue'),
    meta: { requiresAuth: true },
  },

  // Sale admin dashboard — mirrors /admin/sale/dashboard from popinweb
  {
    path: '/sale/dashboard',
    name: 'sale-dashboard',
    component: () => import('@/pages/SaleDashboard.vue'),
    meta: { requiresAuth: true },
  },

  // MTO admin dashboard — mirrors /admin/sale/mto/dashboard from popinweb
  {
    path: '/sale/mto/dashboard',
    name: 'sale-mto-dashboard',
    component: () => import('@/pages/MtoDashboard.vue'),
    meta: { requiresAuth: true },
  },

  // Online admin dashboard — mirrors /admin/sale/measurementdashboard from popinweb
  {
    path: '/online/dashboard',
    name: 'online-dashboard',
    component: () => import('@/pages/OnlineDashboard.vue'),
    meta: { requiresAuth: true },
  },

  // Event admin dashboard — mirrors /admin/event/dashboard from popinweb
  {
    path: '/event/dashboard',
    name: 'event-dashboard',
    component: () => import('@/pages/EventDashboard.vue'),
    meta: { requiresAuth: true },
  },

  // Owner dashboard — rental + sale + event across every branch assigned to
  // the admin, period vs previous period. Backed by /api/admin/owner/dashboard
  // (OwnerDashboardController).
  {
    path: '/owner/dashboard',
    name: 'owner-dashboard',
    component: () => import('@/pages/OwnerDashboard.vue'),
    meta: { requiresAuth: true, adminOnly: true },
  },

  // Event daybook — mirrors /admin/event/daybook/all from popinweb
  {
    path: '/event/daybook/all',
    name: 'event-daybook',
    component: () => import('@/pages/EventDaybook.vue'),
    meta: { requiresAuth: true },
  },

  // Event report — mirrors /admin/event/eventreport from popinweb
  {
    path: '/event/eventreport',
    name: 'event-eventreport',
    component: () => import('@/pages/EventReport.vue'),
    meta: { requiresAuth: true },
  },

  // Event vendor ledger — mirrors /admin/event/vendorledger from popinweb
  {
    path: '/event/vendorledger',
    name: 'event-vendorledger',
    component: () => import('@/pages/EventVendorLedger.vue'),
    meta: { requiresAuth: true },
  },

  // Event sales ledger — mirrors /admin/event/salesledger from popinweb
  {
    path: '/event/salesledger',
    name: 'event-salesledger',
    component: () => import('@/pages/EventSalesLedger.vue'),
    meta: { requiresAuth: true },
  },

  // Rental master menus — mirrors /admin/rental/master from popinweb
  {
    path: '/rental/master',
    name: 'rental-master',
    component: () => import('@/pages/RentalMaster.vue'),
    meta: { requiresAuth: true },
  },

  // Rental QR code catalog — mirrors /admin/rental/qrcode/catalog from popinweb
  // (the "QR Code Print" master tile lands here).
  {
    path: '/rental/qrcode/catalog',
    name: 'rental-qrcode-catalog',
    component: () => import('@/pages/RentalQrCatalog.vue'),
    meta: { requiresAuth: true },
  },

  // Rental daybook — mirrors /admin/rental/daybook/all from popinweb
  {
    path: '/rental/daybook/all',
    name: 'rental-daybook',
    component: () => import('@/pages/RentalDaybook.vue'),
    meta: { requiresAuth: true },
  },

  // Rental return report — mirrors /admin/rental/report/return from popinweb
  {
    path: '/rental/report/return',
    name: 'rental-return-report',
    component: () => import('@/pages/RentalReturnReport.vue'),
    meta: { requiresAuth: true },
  },

  // Rental stich report — mirrors /admin/rental/report/stichreport from popinweb
  {
    path: '/rental/report/stichreport',
    name: 'rental-stich-report',
    component: () => import('@/pages/StitchReport.vue'),
    meta: { requiresAuth: true },
  },

  // Rental daily report — mirrors /admin/rental/report/dailyreport from popinweb
  {
    path: '/rental/report/dailyreport',
    name: 'rental-daily-report',
    component: () => import('@/pages/DailyReport.vue'),
    meta: { requiresAuth: true },
  },

  // Rental sales yearly report — mirrors /admin/rental/report/salesyearlyreport from popinweb
  {
    path: '/rental/report/salesyearlyreport',
    name: 'rental-sales-yearly-report',
    component: () => import('@/pages/SalesYearlyReport.vue'),
    meta: { requiresAuth: true },
  },

  // Rental sales ledger — mirrors /admin/rental/report/salesledger from popinweb
  {
    path: '/rental/report/salesledger',
    name: 'rental-sales-ledger',
    component: () => import('@/pages/SalesLedger.vue'),
    meta: { requiresAuth: true },
  },

  // Rental extra payment — mirrors /admin/rental/report/extrapayment from popinweb
  {
    path: '/rental/report/extrapayment',
    name: 'rental-extra-payment',
    component: () => import('@/pages/ExtraPayment.vue'),
    meta: { requiresAuth: true },
  },

  // Rental GST summary report — mirrors /admin/rental/report/gstsummaryreport from popinweb
  {
    path: '/rental/report/gstsummaryreport',
    name: 'rental-gst-summary-report',
    component: () => import('@/pages/RentalGstSummaryReport.vue'),
    meta: { requiresAuth: true },
  },

  // Rental product purchase report — mirrors /admin/rental/report/productpurchase from popinweb
  {
    path: '/rental/report/productpurchase',
    name: 'rental-product-purchase-report',
    component: () => import('@/pages/RentalProductPurchaseReport.vue'),
    meta: { requiresAuth: true },
  },

  // Rental card holder list — mirrors /admin/rental/report/cardholderlist from popinweb
  {
    path: '/rental/report/cardholderlist',
    name: 'rental-card-holder-report',
    component: () => import('@/pages/RentalCardHolderReport.vue'),
    meta: { requiresAuth: true },
  },

  // Rental customer registration — mirrors /admin/rental/customer/registration from popinweb
  {
    path: '/rental/customer/registration',
    name: 'rental-customer-registration',
    component: () => import('@/pages/RentalCustomerRegistration.vue'),
    meta: { requiresAuth: true },
  },

  // Rental vendor product view — mirrors /admin/rental/vendorproductview/all from popinweb.
  // Wraps the existing `vendorproduct` legacy component already used at /vendor/product.
  shellRoute('/rental/vendorproductview/all', 'rental-vendorproduct-view', 'vendorproduct'),

  // Sale daybook — mirrors /admin/sale/daybook/all from popinweb
  {
    path: '/sale/daybook/all',
    name: 'sale-daybook',
    component: () => import('@/pages/SaleDaybook.vue'),
    meta: { requiresAuth: true },
  },

  // Sale daily report — mirrors /admin/sale/dailyreport from popinweb
  {
    path: '/sale/dailyreport',
    name: 'sale-daily-report',
    component: () => import('@/pages/SaleDailyReport.vue'),
    meta: { requiresAuth: true },
  },

  // Sale brands — mirrors /admin/sale/brands/all from popinweb.
  // Wraps the existing `salesbrand` legacy component already used at /sale/brand.
  shellRoute('/sale/brands/all', 'sale-brands', 'salesbrand'),

  // Sale barcode print — mirrors /admin/sale/report/barcodeprint from popinweb.
  // Wraps the existing `barcodeprint` legacy component already used at /barcode/print.
  shellRoute('/sale/report/barcodeprint', 'sale-barcode-print', 'barcodeprint'),

  // Sale sales ledger — mirrors /admin/sale/report/salesledger from popinweb
  {
    path: '/sale/report/salesledger',
    name: 'sale-sales-ledger',
    component: () => import('@/pages/SaleSalesLedger.vue'),
    meta: { requiresAuth: true },
  },

  // Sale stock report — mirrors /admin/sale/report/stockreport from popinweb
  {
    path: '/sale/report/stockreport',
    name: 'sale-stock-report',
    component: () => import('@/pages/SaleStockReport.vue'),
    meta: { requiresAuth: true },
  },

  // Sale sales yearly dashboard — mirrors /admin/sale/report/salesyearlydashboard from popinweb
  {
    path: '/sale/report/salesyearlydashboard',
    name: 'sale-sales-yearly-dashboard',
    component: () => import('@/pages/SaleSalesYearlyDashboard.vue'),
    meta: { requiresAuth: true },
  },

  // Sale GST summary report — mirrors /admin/sale/report/gstsummaryreport from popinweb
  {
    path: '/sale/report/gstsummaryreport',
    name: 'sale-gst-summary-report',
    component: () => import('@/pages/SaleGstSummaryReport.vue'),
    meta: { requiresAuth: true },
  },

  // Sale extra payment — mirrors /admin/sale/report/extrapayment from popinweb
  {
    path: '/sale/report/extrapayment',
    name: 'sale-extra-payment',
    component: () => import('@/pages/SaleExtraPayment.vue'),
    meta: { requiresAuth: true },
  },

  // Sale GST invoice create — mirrors /admin/sale/gstinvoice/create from popinweb.
  // Wraps the existing `gstinvoicesale` legacy component already used at /gst/invoice/sale.
  shellRoute('/sale/gstinvoice/create', 'sale-gstinvoice-create', 'gstinvoicesale'),

  // Event GST invoice create — mirrors /admin/event/gstinvoice/create from popinweb.
  // Wraps the existing `gstinvoiceevent` legacy component already used at /gst/invoice/event.
  shellRoute('/event/gstinvoice/create', 'event-gstinvoice-create', 'gstinvoiceevent'),

  // Sale pending invoices — mirrors /admin/sale/report/pendinginvoices from popinweb.
  // Wraps the existing `pendinginvoicesale` legacy component already used at /pending/invoices/sale.
  shellRoute('/sale/report/pendinginvoices', 'sale-pending-invoices', 'pendinginvoicesale'),

  // Sale MTO dashboard (make-to-order summary) — mirrors /admin/sale/mtodashboard from popinweb.
  // Wraps the existing `MakeToOrderSummary` legacy component already used at /maketoorder/summary.
  shellRoute('/sale/mtodashboard', 'sale-mtodashboard', 'MakeToOrderSummary'),

  // Sale urgent pending — mirrors /admin/sale/urgentpending from popinweb.
  // Wraps the existing `urgentpending` legacy component already used at /urgent/pending.
  shellRoute('/sale/urgentpending', 'sale-urgentpending', 'urgentpending'),

  // Sale delivery report — mirrors /admin/sale/report/delivery from popinweb
  {
    path: '/sale/report/delivery',
    name: 'sale-delivery-report',
    component: () => import('@/pages/SaleDeliveryReport.vue'),
    meta: { requiresAuth: true },
  },

  // Sale vendor report — mirrors /admin/sale/report/vendorreport from popinweb
  {
    path: '/sale/report/vendorreport',
    name: 'sale-vendor-report',
    component: () => import('@/pages/SaleVendorReport.vue'),
    meta: { requiresAuth: true },
  },

  // Sale MTO daily report — mirrors /admin/sale/dailyreportmto from popinweb
  {
    path: '/sale/dailyreportmto',
    name: 'sale-dailyreport-mto',
    component: () => import('@/pages/SaleDailyReportMto.vue'),
    meta: { requiresAuth: true },
  },

  // Sale tailor customer — mirrors /admin/sale/tailorcustomer from popinweb.
  // Wraps the existing `tailorcustomer` legacy component already used at /tailor/customer.
  shellRoute('/sale/tailorcustomer', 'sale-tailorcustomer', 'tailorcustomer'),

  // Sale online retail bill — mirrors /admin/sale/onlineretailreport from popinweb
  {
    path: '/sale/onlineretailreport',
    name: 'sale-online-retail-report',
    component: () => import('@/pages/SaleOnlineRetailReport.vue'),
    meta: { requiresAuth: true },
  },

  // Rental online delivery report — mirrors /admin/rental/report/onlinedelivery from popinweb
  {
    path: '/rental/report/onlinedelivery',
    name: 'rental-online-delivery-report',
    component: () => import('@/pages/RentalOnlineDeliveryReport.vue'),
    meta: { requiresAuth: true },
  },

  // Rental online return report — mirrors /admin/rental/report/onlinereturn from popinweb
  {
    path: '/rental/report/onlinereturn',
    name: 'rental-online-return-report',
    component: () => import('@/pages/RentalOnlineReturnReport.vue'),
    meta: { requiresAuth: true },
  },

  // Rental online booking report — mirrors /admin/rental/report/onlinebooking from popinweb
  {
    path: '/rental/report/onlinebooking',
    name: 'rental-online-booking-report',
    component: () => import('@/pages/RentalOnlineBookingReport.vue'),
    meta: { requiresAuth: true },
  },

  // Mirror of the registrations in popinwebapp/resources/js/app.js.
  shellRoute('/example',                       'example',                       'ExampleComponent'),
  shellRoute('/receipt',                       'receipt',                       'receipt'),
  shellRoute('/payment',                       'payment',                       'payment'),
  shellRoute('/attendance',                    'attendance',                    'attendance'),
  shellRoute('/attendance/view',               'attendanceview',                'attendanceview'),
  shellRoute('/calllog',                       'calllog',                       'calllog'),
  shellRoute('/booking',                       'booking',                       'booking'),
  shellRoute('/booking/event',                 'eventbooking',                  'eventbooking'),
  shellRoute('/booking/event-v2',              'eventbooking1',                 'eventbooking1'),
  shellRoute('/selection',                     'selection',                     'selection'),
  shellRoute('/selection/event',               'selectionevent',                'selectionevent'),
  shellRoute('/selection/sales',               'selectionsales',                'selectionsales'),
  shellRoute('/selection/sales-v2',            'selectionsales1',               'selectionsales1'),
  shellRoute('/sale',                          'sale',                          'sale'),
  shellRoute('/sale/men',                      'salem',                         'salem', () => ({ salestype: 2 })),

  // Information & Policy pages — linked from the side drawer. All reuse the
  // shared InfoPage component; content is keyed by pageKey (see infoPages.js).
  shellRoute('/about-us',            'about-us',            'InfoPage', () => ({ pageKey: 'about' })),
  shellRoute('/contact-us',          'contact-us',          'ContactPage'),
  shellRoute('/store',               'store',               'StorePage'),
  shellRoute('/blog',                'blog',                'BlogPage'),
  shellRoute('/faq',                 'faq',                 'InfoPage', () => ({ pageKey: 'faq' })),
  shellRoute('/franchise-enquiry',   'franchise-enquiry',   'FranchisePage'),
  shellRoute('/cancellation-policy', 'cancellation-policy', 'InfoPage', () => ({ pageKey: 'cancellation-policy' })),
  shellRoute('/privacy-policy',      'privacy-policy',      'InfoPage', () => ({ pageKey: 'privacy-policy' })),
  shellRoute('/terms-conditions',    'terms-conditions',    'InfoPage', () => ({ pageKey: 'terms' })),
  shellRoute('/sale/brand',                    'salesbrand',                    'salesbrand'),
  shellRoute('/sale/man',                      'salesman',                      'salesman'),
  shellRoute('/rental/salesmans/view',         'rental-salesmans-view',         'salesman'),
  shellRoute('/sale/man-v2',                   'salesman-1',                    'salesman-1'),
  shellRoute('/sale/product',                  'productsale',                   'productsale'),
  shellRoute('/sale/receipt',                  'receiptsales',                  'receiptsales'),
  shellRoute('/sale/payment',                  'paymentsales',                  'paymentsales'),
  shellRoute('/event/product',                 'eventproduct',                  'eventproduct'),
  shellRoute('/event/receipt',                 'receiptevent',                  'receiptevent'),
  shellRoute('/event/receipts/all',            'event-receipts-all',            'receiptevent'),
  shellRoute('/event/payment',                 'paymentevent',                  'paymentevent'),
  shellRoute('/event/payments/all',            'event-payments-all',            'paymentevent'),
  shellRoute('/product',                       'product',                       'product'),
  shellRoute('/product/to-image',              'producttoimage',                'producttoimage'),
  shellRoute('/userright',                     'userright',                     'userright'),
  shellRoute('/userright/desktop',             'userrightdesktop',              'userrightdesktop'),
  shellRoute('/category',                      'category',                      'category'),
  shellRoute('/category/rental',               'categoryrental',                'categoryrental'),
  shellRoute('/category/sale',                 'categorysale',                  'categorysale'),
  shellRoute('/category/mto',                  'categorymto',                   'categorymto'),
  shellRoute('/menus',                         'menus',                         'menus'),
  shellRoute('/colour',                        'colour',                        'colour'),
  shellRoute('/rental/colours',                'rental-colours',                'colour'),
  shellRoute('/size',                          'size',                          'size'),
  shellRoute('/company',                       'company',                       'company'),
  shellRoute('/bg-image',                      'bgimage',                       'BGImageComponent'),
  shellRoute('/banner-image',                  'bannerimage',                   'BannerComponent'),
  // /measurement points at the Measurement MASTER page. MeasurementComponent is
  // a modal-only component (needs unqid/srno/mobile props) and renders blank as
  // a standalone route — the MTO "Measurment" tile lands here, so serve the real
  // master page instead.
  shellRoute('/measurement',                   'measurementcomponent',          'MeasurementMaster'),
  shellRoute('/measurement/master',            'measurementmaster',             'MeasurementMaster'),
  shellRoute('/maketoorder/summary',           'maketoordersummary',            'MakeToOrderSummary'),
  shellRoute('/finishwash',                    'finishwash',                    'finishwash'),
  shellRoute('/finish',                        'finish',                        'finish'),
  shellRoute('/cutting',                       'cutting',                       'cutting'),
  shellRoute('/cutting/v2',                    'cutting1',                      'cutting1'),
  shellRoute('/tailor/customer',               'tailorcustomer',                'tailorcustomer'),
  shellRoute('/urgent/pending',                'urgentpending',                 'urgentpending'),
  shellRoute('/customer/history',              'customerhistory',               'customerhistory'),
  shellRoute('/customer/create',               'customercreate',                'customercreate'),
  shellRoute('/customer/measurement',          'customermeasurment',            'customermeasurment'),
  shellRoute('/customer/measurement/jobworker','customermeasurmentjobworker',   'customermeasurmentjobworker'),
  shellRoute('/customer/measurement/urgent',   'customermeasurmenturgent',      'customermeasurmenturgent'),
  shellRoute('/customer/measurement/finish',   'customermeasurmentfinish',      'customermeasurmentfinish'),
  shellRoute('/pending/invoices',              'pendinginvoice',                'pendinginvoice'),
  shellRoute('/pending/invoices/sale',         'pendinginvoicesale',            'pendinginvoicesale'),
  // Rental delivery report — modernized SPA page (mirrors filter-form pattern)
  {
    path: '/delivery/report',
    name: 'deliveryreport',
    component: () => import('@/pages/RentalDeliveryReport.vue'),
    meta: { requiresAuth: true },
  },

  // Rental monthly delivery pcs report — mirrors /admin/rental/report/deliverymonthly from popinweb
  {
    path: '/rental/report/deliverymonthly',
    name: 'rental-delivery-monthly',
    component: () => import('@/pages/RentalDeliveryMonthly.vue'),
    meta: { requiresAuth: true },
  },

  // Rental deposit report — mirrors /admin/rental/report/depositreport from popinweb
  {
    path: '/rental/report/depositreport',
    name: 'rental-deposit-report',
    component: () => import('@/pages/RentalDepositReport.vue'),
    meta: { requiresAuth: true },
  },

  // Day End & Backup — consolidated closing summary across the rental/sale/event
  // daybooks, cash reconciliation, and a backup of the day's data. Backed by
  // `/api/admin/dayend`, `/api/admin/dayend/close` and `/api/admin/dayend/backup`
  // (DayEndController); localStorage is only the offline fallback.
  {
    path: '/dayend',
    name: 'day-end-backup',
    component: () => import('@/pages/DayEndBackup.vue'),
    meta: { requiresAuth: true },
  },

  // Salesman Booking Slip — products picked like /selection, saved + printed;
  // the Rental Bill screen pulls today's slip in by customer mobile.
  {
    path: '/booking-slip',
    name: 'booking-slip',
    component: () => import('@/pages/BookingSlip.vue'),
    meta: { requiresAuth: true },
  },

  // Purchase Entry — supplier bills with full GST split, entered manually or
  // read from an uploaded invoice by AI; prints a purchase voucher. Backed by
  // `/api/admin/purchase/*` (PurchaseController). `?id=` opens a saved entry.
  {
    path: '/purchase/entry',
    name: 'purchase-entry',
    component: () => import('@/pages/PurchaseEntry.vue'),
    meta: { requiresAuth: true },
  },

  // Purchase Report — bill / item / supplier / HSN / GST rate / branch / month
  // views over the same tables, company-wise or across the admin's branches.
  {
    path: '/purchase/report',
    name: 'purchase-report',
    component: () => import('@/pages/PurchaseReport.vue'),
    meta: { requiresAuth: true },
  },

  // Notifications — inbox for every admin user; Admins / Branch Managers also
  // get Send + Sent tabs. Backed by `/api/admin/notifications*`
  // (NotificationController). The header bell polls the unread count.
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('@/pages/NotificationsPage.vue'),
    meta: { requiresAuth: true },
  },

  // WhatsApp — send messages through the WhatsApp Hub V3 API, the send log,
  // and the API URL / key settings. Admin role only. Backed by
  // `/api/admin/whatsapp/*` (WhatsappHubController).
  {
    path: '/whatsapp',
    name: 'whatsapp',
    component: () => import('@/pages/WhatsappHub.vue'),
    meta: { requiresAuth: true, adminOnly: true },
  },

  // Licences — issue/manage product licences. UI persists locally until the
  // backend `/api/admin/licences` endpoints exist (see LicencePage.vue NOTEs).
  {
    path: '/licences',
    name: 'licences',
    component: () => import('@/pages/LicencePage.vue'),
    meta: { requiresAuth: true },
  },
  shellRoute('/gst/invoice',                   'gstinvoice',                    'gstinvoice'),
  shellRoute('/gst/invoice/event',             'gstinvoiceevent',               'gstinvoiceevent'),
  shellRoute('/gst/invoice/sale',              'gstinvoicesale',                'gstinvoicesale'),
  shellRoute('/salary/print',                  'salaryprint',                   'salaryprint'),
  shellRoute('/barcode/print',                 'barcodeprint',                  'barcodeprint'),

  // Chat
  shellRoute('/chat',                          'chatapp',                       'ChatApp'),
  shellRoute('/chat/box',                      'chat-box',                      'ChatBox'),

  // Vendor
  shellRoute('/vendor',                        'vendor',                        'vendor'),
  shellRoute('/vendor/event',                  'vendorevent',                   'vendorevent'),
  shellRoute('/vendor/product',                'vendorproduct',                 'vendorproduct'),
  shellRoute('/vendor/order/list',             'vendororderlist',               'vendororderlist'),
  shellRoute('/rental/vendororderlist/all',    'rental-vendororderlist-all',    'vendororderlist'),

  // Ecommerce / dashboards
  shellRoute('/ecommerce/dashboard',           'ecommerce-dashboard',           'EcommerceDashboard'),
  shellRoute('/ecommerce/orders',              'order-management',              'OrderManagement'),
  shellRoute('/ecommerce/online-cart',         'online-cart',                   'OnlineCart'),
  shellRoute('/ecommerce/online-carts',        'online-carts',                  'OnlineCart'),
  shellRoute('/ecommerce/technology-partners', 'technology-partners',           'TechnologyPartners'),
  // Product dashboard content now lives on the rental overview.
  { path: '/ecommerce/product-dashboard', redirect: '/rental-overview/dashboard' },
  shellRoute('/ecommerce/rental-dashboard',    'ecommerce-rental-dashboard',    'RentalDashboard'),
  shellRoute('/ecommerce/activity-summary',    'activity-summary',              'ActivitySummary'),

  // Master / Rental analytics dashboard — mirrors /admin/rental-overview/dashboard from popinweb.
  {
    path: '/rental-overview/dashboard',
    name: 'rental-overview-dashboard',
    component: () => import('@/pages/RentalOverviewDashboard.vue'),
    meta: { requiresAuth: true },
  },

  // Profile / misc
  shellRoute('/profile',                       'profile-view',                  'ProfileView'),
  shellRoute('/profile/modal',                 'profile-view-modal',            'ProfileViewModal'),

  // Catch-all → home
  { path: '*', redirect: '/rental/dashboard' },
]
